import { Controller } from "@hotwired/stimulus"

// Interactive savings calculator for the Money Math section. Fern's leads are
// additive to the leads the site already produces.
//
// The rates arrive as values from Fern::Assumptions rather than living here —
// this controller used to hold its own copy, which is how the live calculator
// drifted away from both the server-rendered defaults and the headline claims
// above it. Every figure on the page now comes from one place.
export default class extends Controller {
  static targets = [
    "adCost", "visitors", "convRate",
    "adCostLabel", "visitorsLabel", "convRateLabel",
    "currentLeads", "currentCpl",
    "chats", "chatsCost", "fernLeads", "booksCost", "fernCost", "fernCpl",
    "totalLeads", "newCpl", "savings", "savingsBar"
  ]

  static values = {
    costPerChat: Number,
    costPerLead: Number,
    hostingFee: Number,
    visitorToChat: Number,
    chatToLead: Number
  }

  connect() {
    this.recalculate()
  }

  recalculate() {
    const adCost = parseFloat(this.adCostTarget.value)
    const visitors = parseFloat(this.visitorsTarget.value)
    const convRate = parseFloat(this.convRateTarget.value) / 100

    this.adCostLabelTarget.textContent = this.money(adCost, 0)
    this.visitorsLabelTarget.textContent = visitors.toLocaleString()
    this.convRateLabelTarget.textContent = `${(convRate * 100).toFixed(1)}%`

    // whole chats/leads so the itemized invoice always adds up exactly
    const currentLeads = Math.round(visitors * convRate)
    const currentCpl = currentLeads > 0 ? adCost / currentLeads : null

    const chats = Math.round(visitors * this.visitorToChatValue)
    const fernLeads = Math.round(chats * this.chatToLeadValue)
    const chatsCost = chats * this.costPerChatValue
    const booksCost = fernLeads * this.costPerLeadValue
    const fernCost = chatsCost + booksCost + this.hostingFeeValue
    const fernCpl = fernLeads > 0 ? fernCost / fernLeads : null

    const totalLeads = currentLeads + fernLeads
    const newCpl = totalLeads > 0 ? (adCost + fernCost) / totalLeads : null
    const savings = currentCpl && newCpl ? Math.max(0, 1 - newCpl / currentCpl) : null

    this.currentLeadsTarget.textContent = this.round(currentLeads)
    this.currentCplTarget.textContent = currentCpl ? this.money(currentCpl) : "—"
    this.chatsTarget.textContent = this.round(chats)
    this.chatsCostTarget.textContent = this.money(chatsCost, 0)
    this.fernLeadsTargets.forEach((el) => { el.textContent = this.round(fernLeads) })
    this.booksCostTarget.textContent = this.money(booksCost, 0)
    this.fernCostTarget.textContent = this.money(fernCost, 0)
    this.fernCplTarget.textContent = fernCpl ? this.money(fernCpl) : "—"
    this.totalLeadsTarget.textContent = this.round(totalLeads)
    this.newCplTarget.textContent = newCpl ? this.money(newCpl) : "—"
    this.savingsTarget.textContent = savings === null ? "—" : `${Math.round(savings * 100)}%`
    this.savingsBarTarget.style.width = savings === null ? "0%" : `${Math.min(100, Math.round(savings * 100))}%`
  }

  round(value) {
    return Math.round(value).toLocaleString()
  }

  money(value, decimals = 2) {
    const rounded = value.toFixed(decimals)
    const clean = decimals > 0 && rounded.endsWith("." + "0".repeat(decimals)) ? value.toFixed(0) : rounded
    return `$${parseFloat(clean).toLocaleString(undefined, { minimumFractionDigits: clean.includes(".") ? decimals : 0 })}`
  }
}
